import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { scheduleAPI, tweetsAPI } from '../api';
import { format, parseISO } from 'date-fns';
import { ja } from 'date-fns/locale';

function AddScheduleModal({ onClose, onSaved }) {
  const [content, setContent] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [saving, setSaving] = useState(false);
  const [libraryTweets, setLibraryTweets] = useState([]);
  const [selectedFromLib, setSelectedFromLib] = useState('');

  useEffect(() => {
    tweetsAPI.getAll({ status: 'ready' }).then(r => setLibraryTweets(r.data)).catch(() => {});
  }, []);

  const handleSelectLib = (e) => {
    const id = e.target.value;
    setSelectedFromLib(id);
    const tw = libraryTweets.find(t => t.id === id);
    if (tw) setContent(tw.content);
  };

  const minDate = new Date(Date.now() + 60000);
  const minStr = format(minDate, "yyyy-MM-dd'T'HH:mm");

  const handleSave = async () => {
    if (!content.trim()) return toast.error('内容を入力してください');
    if (!scheduledAt) return toast.error('日時を選択してください');
    if (content.length > 280) return toast.error('280文字を超えています');
    setSaving(true);
    try {
      await scheduleAPI.create({
        content,
        scheduled_at: scheduledAt,
        tweet_id: selectedFromLib || null,
      });
      toast.success('スケジュール設定しました');
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.error || 'エラーが発生しました');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">📅 新規スケジュール</div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {libraryTweets.length > 0 && (
          <div className="form-group">
            <label className="form-label">ライブラリから選択（任意）</label>
            <select className="select" value={selectedFromLib} onChange={handleSelectLib}>
              <option value="">-- 手動で入力 --</option>
              {libraryTweets.map(tw => (
                <option key={tw.id} value={tw.id}>{tw.content.slice(0, 50)}...</option>
              ))}
            </select>
          </div>
        )}

        <div className="form-group">
          <label className="form-label">ツイート内容</label>
          <textarea
            className="textarea"
            value={content}
            onChange={e => setContent(e.target.value)}
            placeholder="ツイート内容を入力..."
            rows={5}
          />
          <div className={`char-counter ${content.length > 280 ? 'over' : content.length > 252 ? 'warn' : ''}`}>
            {content.length} / 280
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">投稿日時</label>
          <input type="datetime-local" className="input" value={scheduledAt} min={minStr} onChange={e => setScheduledAt(e.target.value)} />
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost" onClick={onClose}>キャンセル</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? '設定中...' : 'スケジュール設定'}
          </button>
        </div>
      </div>
    </div>
  );
}

const STATUS_LABELS = {
  pending: { label: '待機中', badge: 'pending' },
  posted: { label: '投稿済み', badge: 'posted' },
  failed: { label: '失敗', badge: 'failed' },
  cancelled: { label: 'キャンセル', badge: 'draft' },
};

export default function SchedulePage() {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [filter, setFilter] = useState('pending');

  const load = async () => {
    setLoading(true);
    try {
      const res = await scheduleAPI.getAll();
      setSchedules(res.data);
    } catch (e) {
      toast.error('読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleCancel = async (id) => {
    if (!window.confirm('キャンセルしますか？')) return;
    try {
      await scheduleAPI.cancel(id);
      toast.success('キャンセルしました');
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || 'エラーが発生しました');
    }
  };

  const filtered = schedules.filter(s => filter === 'all' || s.status === filter);

  const pending = schedules.filter(s => s.status === 'pending');
  const posted = schedules.filter(s => s.status === 'posted');

  return (
    <div>
      <div className="page-header">
        <div className="page-title">🗓️ スケジュール管理</div>
        <div className="page-sub">予約投稿の確認・追加・キャンセル</div>
      </div>

      <div className="grid-3" style={{ marginBottom: 24 }}>
        <div className="stat-card">
          <div className="stat-value" style={{ color: 'var(--warning)' }}>{pending.length}</div>
          <div className="stat-label">待機中</div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={{ color: 'var(--success)' }}>{posted.length}</div>
          <div className="stat-label">投稿済み</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{schedules.length}</div>
          <div className="stat-label">合計</div>
        </div>
      </div>

      <div className="toolbar">
        <select className="select" style={{ width: 140 }} value={filter} onChange={e => setFilter(e.target.value)}>
          <option value="all">すべて</option>
          <option value="pending">待機中</option>
          <option value="posted">投稿済み</option>
          <option value="failed">失敗</option>
          <option value="cancelled">キャンセル済み</option>
        </select>
        <div className="toolbar-spacer" />
        <button className="btn btn-primary" onClick={() => setShowAdd(true)}>＋ スケジュール追加</button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>読み込み中...</div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📅</div>
          <div className="empty-state-text">スケジュールがありません</div>
        </div>
      ) : (
        filtered.map(item => {
          const statusInfo = STATUS_LABELS[item.status] || { label: item.status, badge: 'draft' };
          let dateStr = item.scheduled_at;
          try {
            dateStr = format(parseISO(item.scheduled_at), 'yyyy年M月d日 HH:mm', { locale: ja });
          } catch {}

          return (
            <div key={item.id} className="tweet-card">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span className={`badge badge-${statusInfo.badge}`}>{statusInfo.label}</span>
                <span style={{ fontSize: 13, color: 'var(--accent)', fontWeight: 500 }}>🕐 {dateStr}</span>
              </div>
              <div className="tweet-card-content">{item.content}</div>
              {item.error_message && (
                <div style={{ marginTop: 8, fontSize: 12, color: 'var(--danger)', background: 'var(--danger-dim)', padding: '6px 10px', borderRadius: 6 }}>
                  ❌ {item.error_message}
                </div>
              )}
              {item.status === 'pending' && (
                <div style={{ marginTop: 10 }}>
                  <button className="btn btn-sm btn-danger" onClick={() => handleCancel(item.id)}>キャンセル</button>
                </div>
              )}
            </div>
          );
        })
      )}

      {showAdd && <AddScheduleModal onClose={() => setShowAdd(false)} onSaved={load} />}
    </div>
  );
}
